import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { getEventsLOV, type EventSummary } from 'api/events/operations/getEventsLOV';

import { EditReference } from './EditReference';

interface Props {
  // The stored event_id, or null for "no event".
  readonly value: number | null;
  readonly onChange: (value: number | null) => void;
  readonly label?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
}

const getKey = (e: EventSummary) => e.event_id;
const getLabel = (e: EventSummary): string => e.name;

/**
 * Searchable dropdown for (process) events keyed on `event_id`, for callers that store the numeric id — the same shape as
 * {@link EditSchedulingWindowIdReference}. The id↔display-name expansion is internal: the caller works in plain ids, and the
 * eager LOV resolves a stored id to its name via `findByKey`. Selection returns the id.
 */
export const EditEventReference = ({ value, onChange, label = 'Event', mandatory, disabled }: Props) => {
  // Inflate the stored id to the partial summary EditReference selects on; the empty name defers label resolution to the
  // eager LOV's findByKey. Memoised on the id so the reference model isn't rebuilt each render.
  const selected = useMemo<EventSummary | null>(() => (value !== null ? { event_id: value, name: '', zone_name: '' } : null), [value]);

  return (
    <EditReference<EventSummary>
      label={label}
      value={selected}
      onChange={(e) => onChange(e?.event_id ?? null)}
      source={getEventsLOV}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(e, query) => (
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{e.name}</Highlighter>
          </span>
          <span className="text-body-secondary">{e.zone_name}</span>
        </div>
      )}
    />
  );
};
